import { usePlanBuilder, type AdminPlan } from '@church/data'
import { formatCents, type Cents } from '@church/domain'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  Button,
  Card,
  ConfirmButton,
  ErrorNote,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from '../components/ui.tsx'

/**
 * The plan builder (Phase 12f): plans are columns, the features and limits
 * the code enforces are rows. Changes collect until Save, which applies them
 * to every church on those plans at once and makes Stripe sell each paid plan
 * at its price.
 */
export const Route = createFileRoute('/_authed/admin/plans')({
  component: PlanBuilder,
})

const APP_NAMES = { church: 'ForChurch', services: 'ForServices', stream: 'ForStream' } as const

function PlanBuilder() {
  const b = usePlanBuilder()
  const data = b.plans.data
  if (b.plans.isPending) return <Spinner />
  if (!data) return <ErrorNote error={b.plans.error} />
  const live = data.plans.filter((p) => !p.archivedAt)
  const retired = data.plans.filter((p) => p.archivedAt)
  const { features, limits } = data.catalog

  return (
    <div className="space-y-6 pb-24">
      <PageHeader visible title="Plans" />
      <ErrorNote error={b.save.error ?? b.create.error ?? b.archive.error ?? b.setRoles.error} />
      {Object.entries(b.syncErrors).map(([id, message]) => (
        <p key={id} className="rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-content">
          Saved, but Stripe did not take {data.plans.find((p) => p.id === id)?.name}: {message}
        </p>
      ))}
      <Roles
        plans={live}
        onSave={(body) => b.setRoles.mutate(body)}
        saving={b.setRoles.isPending}
      />

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 w-56 border-b border-border-subtle bg-surface-raised px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-content-subtle">
                Plan
              </th>
              {live.map((plan) => (
                <PlanHeader key={plan.id} plan={plan} b={b} />
              ))}
            </tr>
          </thead>
          <tbody>
            {(['church', 'services', 'stream'] as const).map((app) => (
              <AppRows key={app} app={app} plans={live} b={b} features={features} limits={limits} />
            ))}
          </tbody>
        </table>
      </Card>

      <NewPlan onCreate={(body) => b.create.mutate(body)} creating={b.create.isPending} />

      {retired.length > 0 && (
        <Card className="space-y-2 p-4 text-sm">
          <h2 className="font-semibold text-content-default">Retired plans</h2>
          <p className="text-content-muted">No new churches; churches already on them keep them.</p>
          {retired.map((p) => (
            <div key={p.id} className="flex items-center justify-between">
              <span>
                {p.name} · {price(p.monthlyCents)}
              </span>
              <Button
                variant="ghost"
                onClick={() => b.archive.mutate({ id: p.id, archived: false })}
              >
                Bring back
              </Button>
            </div>
          ))}
        </Card>
      )}

      {b.dirty.length > 0 && (
        <div className="safe-bottom fixed inset-x-0 bottom-0 border-t border-border-subtle bg-surface-raised/95 px-6 backdrop-blur">
          <div className="mx-auto flex max-w-7xl items-center gap-3 py-3">
            <span className="text-sm text-content-default">
              {b.dirty.length} {b.dirty.length === 1 ? 'plan' : 'plans'} changed
            </span>
            <Button className="ml-auto" variant="secondary" onClick={b.discard}>
              Discard
            </Button>
            <Button disabled={b.save.isPending} onClick={() => b.save.mutate()}>
              {b.save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

const price = (cents: number) =>
  cents === 0 ? 'Free' : `${formatCents(cents as Cents).replace(/\.00$/, '')}/mo`

type Builder = ReturnType<typeof usePlanBuilder>
type Catalog = NonNullable<Builder['plans']['data']>['catalog']

function PlanHeader({ plan, b }: { plan: AdminPlan; b: Builder }) {
  const d = b.draft(plan)
  const changed = b.dirty.includes(plan.id)
  return (
    <th
      className={`min-w-44 border-b border-l border-border-subtle px-3 py-3 text-left align-top font-normal ${changed ? 'bg-brand-soft' : ''}`}
    >
      <Input
        aria-label="Plan name"
        value={d.name}
        onChange={(e) => b.edit(plan, (x) => ({ ...x, name: e.target.value }))}
        className="font-semibold"
      />
      <div className="mt-2 flex items-center gap-1 text-xs text-content-muted">
        $
        <Input
          aria-label={`${plan.name} monthly price, dollars`}
          type="number"
          min={0}
          step={1}
          value={d.monthlyCents / 100}
          onChange={(e) =>
            b.edit(plan, (x) => ({ ...x, monthlyCents: Math.round(Number(e.target.value) * 100) }))
          }
          className="w-20"
        />
        /mo
      </div>
      <div className="mt-1 flex items-center gap-1 text-xs text-content-muted">
        <Input
          aria-label={`${plan.name} trial days`}
          type="number"
          min={0}
          value={d.trialDays}
          onChange={(e) => b.edit(plan, (x) => ({ ...x, trialDays: Number(e.target.value) }))}
          className="w-16"
        />
        trial days
      </div>
      <label className="mt-2 flex items-center gap-1.5 text-xs text-content-muted">
        <input
          type="checkbox"
          checked={d.public}
          onChange={(e) => b.edit(plan, (x) => ({ ...x, public: e.target.checked }))}
        />
        Offered to churches
      </label>
      <div className="mt-2 flex flex-wrap gap-1">
        {plan.isDefault && <Badge>Free fallback</Badge>}
        {plan.isSignup && <Badge>New churches</Badge>}
        <span className="text-xs text-content-subtle">{plan.key}</span>
      </div>
      {!plan.isDefault && !plan.isSignup && (
        <ConfirmButton
          className="mt-2 px-0 text-xs"
          label="Retire"
          title={`Retire ${plan.name}?`}
          description="No new churches can choose it. Churches on it now keep it."
          confirmLabel="Retire it"
          onConfirm={() => b.archive.mutate({ id: plan.id, archived: true })}
        />
      )}
    </th>
  )
}

function Badge({ children }: { children: string }) {
  return (
    <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-xs text-content-default">
      {children}
    </span>
  )
}

function AppRows({
  app,
  plans,
  b,
  features,
  limits,
}: {
  app: 'church' | 'services' | 'stream'
  plans: AdminPlan[]
  b: Builder
  features: Catalog['features']
  limits: Catalog['limits']
}) {
  const rowFeatures = features.filter((f) => f.app === app)
  const rowLimits = limits.filter((l) => l.app === app)
  return (
    <>
      <tr>
        <th
          colSpan={plans.length + 1}
          className="sticky left-0 border-b border-border-subtle bg-surface-sunken px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-default"
        >
          {APP_NAMES[app]}
        </th>
      </tr>
      {rowFeatures.map((f) => (
        <tr key={f.key} className="hover:bg-surface-sunken/50">
          <th className="sticky left-0 border-b border-border-subtle bg-surface-raised px-4 py-2 text-left font-normal">
            <span className="block text-content-default">{f.label}</span>
            <span className="text-xs text-content-subtle">{f.description}</span>
          </th>
          {plans.map((plan) => {
            const on = b.draft(plan).features.includes(f.key)
            return (
              <td
                key={plan.id}
                className="border-b border-l border-border-subtle px-3 py-2 text-center"
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={`${f.label} in ${plan.name}`}
                  onClick={() => b.toggleFeature(plan, f.key)}
                  className={`inline-flex h-6 w-11 items-center rounded-full p-0.5 transition-colors ${on ? 'bg-brand-solid' : 'bg-border-strong'}`}
                >
                  <span
                    className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : ''}`}
                  />
                </button>
              </td>
            )
          })}
        </tr>
      ))}
      {rowLimits.map((l) => (
        <tr key={l.key}>
          <th className="sticky left-0 border-b border-border-subtle bg-surface-raised px-4 py-2 text-left font-normal">
            <span className="block text-content-default">{l.label}</span>
            <span className="text-xs text-content-subtle">{l.unit}; empty is unlimited</span>
          </th>
          {plans.map((plan) => {
            const value = b.draft(plan).limits[l.key]
            return (
              <td key={plan.id} className="border-b border-l border-border-subtle px-3 py-2">
                <Input
                  aria-label={`${l.label} in ${plan.name}`}
                  type="number"
                  min={0}
                  step={1}
                  placeholder="∞"
                  value={value === null || value === undefined ? '' : value}
                  onChange={(e) =>
                    b.setLimit(plan, l.key, e.target.value === '' ? null : Number(e.target.value))
                  }
                  className="w-24"
                />
              </td>
            )
          })}
        </tr>
      ))}
    </>
  )
}

function Roles({
  plans,
  onSave,
  saving,
}: {
  plans: AdminPlan[]
  onSave: (body: { defaultPlanId: string; signupPlanId: string }) => void
  saving: boolean
}) {
  const [free, setFree] = useState(plans.find((p) => p.isDefault)?.id ?? '')
  const [signup, setSignup] = useState(plans.find((p) => p.isSignup)?.id ?? '')
  const unchanged =
    free === plans.find((p) => p.isDefault)?.id && signup === plans.find((p) => p.isSignup)?.id
  return (
    <Card className="flex flex-wrap items-end gap-4 p-4">
      <Field label="Free plan (where lapsed trials and cancelled plans land)">
        <Select value={free} onChange={(e) => setFree(e.target.value)}>
          {plans
            .filter((p) => p.monthlyCents === 0)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </Select>
      </Field>
      <Field label="New churches start on (a trial of its trial days)">
        <Select value={signup} onChange={(e) => setSignup(e.target.value)}>
          {plans.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.trialDays ? ` (${p.trialDays}-day trial)` : ''}
            </option>
          ))}
        </Select>
      </Field>
      <Button
        variant="secondary"
        disabled={unchanged || !free || !signup || saving}
        onClick={() => onSave({ defaultPlanId: free, signupPlanId: signup })}
      >
        Save
      </Button>
    </Card>
  )
}

function NewPlan({
  onCreate,
  creating,
}: {
  onCreate: (body: { key: string; name: string; monthlyCents: number }) => void
  creating: boolean
}) {
  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [dollars, setDollars] = useState('')
  const slug =
    key ||
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
  return (
    <Card className="flex flex-wrap items-end gap-3 p-4">
      <Field label="New plan">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enterprise" />
      </Field>
      <Field label="Key (never changes)">
        <Input value={slug} onChange={(e) => setKey(e.target.value)} placeholder="enterprise" />
      </Field>
      <Field label="Price, $ a month">
        <Input
          type="number"
          min={0}
          value={dollars}
          onChange={(e) => setDollars(e.target.value)}
          className="w-28"
        />
      </Field>
      <Button
        variant="secondary"
        disabled={!name.trim() || !/^[a-z][a-z0-9-]{1,31}$/.test(slug) || creating}
        onClick={() => {
          onCreate({
            key: slug,
            name: name.trim(),
            monthlyCents: Math.round(Number(dollars || 0) * 100),
          })
          setName('')
          setKey('')
          setDollars('')
        }}
      >
        Add plan
      </Button>
    </Card>
  )
}
